import { useState, type MouseEvent, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MoreHorizontal, SquareArrowOutUpRight, Trash2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "~/components/ui/table";
import { cn } from "~/lib/utils";
import { nextgenAdminEvalRunPath } from "~/pages/manifold-nextgen/paths";
import { evalRunPath, type PartialEvalRun } from "~/services/evals";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog";
import { CellSkeleton } from "./EvalSkeletons";
import { EvalTable, rowActivateProps, SortableHead } from "./EvalTable";
import { useTableSort } from "./TableSort";
import { RUN_SORT_DIRECTIONS, RunSortKey } from "./constants/sorting";
import { RunStatusBadge } from "./primitives/badges";
import { TagChips } from "./primitives/chips";
import { ScoreValue } from "./primitives/figures";
import { formatDuration, formatEvalTimestamp, shortModelName } from "./primitives/format";
import { parseApiTimestamp } from "./selectors/core";
import { type RunSummary, type RunTableRow, sortRunRows } from "./selectors/runs";
import type { RunAgentLabels } from "./useEvalApi";

/**
 * A run outside the measured window. Not the `—` the measured columns use for
 * "nothing there": these two say opposite things.
 */
export const NotMeasured = () => (
  <span
    className="text-muted-foreground/50"
    title="Outcomes load for the most recent runs. Open this run to see its results."
  >
    ·
  </span>
);

/**
 * Recent runs, one row each. Sorting is client-side: the page already holds the
 * window it plots, and re-fetching would break the tie to the trend above.
 */
export const RunsTable = ({
  rows,
  agentLabels,
  resultsLoading,
  onDeleted,
}: {
  rows: RunTableRow[];
  agentLabels: RunAgentLabels;
  /**
   * Whether the per-run results are still arriving. A run's own record carries
   * no outcome, so the three measured columns come from a second request and
   * are unknown for a few seconds after the rest of the row is on screen.
   */
  resultsLoading: boolean;
  onDeleted: () => void;
}) => {
  const sort = useTableSort<RunSortKey>(RunSortKey.STARTED, "desc", RUN_SORT_DIRECTIONS);
  const sorted = sortRunRows(rows, sort.sortKey, sort.direction);
  const [pendingDelete, setPendingDelete] = useState<PartialEvalRun | null>(null);
  const navigate = useNavigate();

  // The row navigates, so it honours the modifier a link would: cmd/ctrl-click
  // opens the run in a new tab rather than replacing the table you are scanning.
  const openRun = (runId: string) => (event: MouseEvent<HTMLElement>) => {
    const path = nextgenAdminEvalRunPath(runId);
    if (event.metaKey || event.ctrlKey) {
      window.open(path, "_blank", "noopener");
      return;
    }
    navigate(path);
  };

  return (
    <>
      <EvalTable>
        <TableHeader>
          <TableRow>
            <SortableHead columnKey={RunSortKey.STARTED} label="Started" sort={sort} />
            <SortableHead columnKey={RunSortKey.STATUS} label="Status" sort={sort} />
            <TableHead>Agent</TableHead>
            <TableHead>Case selector</TableHead>
            <SortableHead
              columnKey={RunSortKey.MEAN_SCORE}
              label="Mean score"
              alignRight
              sort={sort}
            />
            <SortableHead columnKey={RunSortKey.CASES} label="Cases" alignRight sort={sort} />
            <TableHead className="text-right">Iterations</TableHead>
            <TableHead className="text-right">Max concurrency</TableHead>
            <SortableHead columnKey={RunSortKey.DURATION} label="Duration" alignRight sort={sort} />
            <TableHead className="w-8" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map(({ run, summary, durationSeconds }) => {
            // The measured columns share one gate, so they state it once.
            const outcome = (render: (summary: RunSummary) => ReactNode, skeleton: string) =>
              resultsLoading ? (
                <CellSkeleton className={skeleton} />
              ) : summary == null ? (
                <NotMeasured />
              ) : (
                render(summary)
              );
            return (
              <TableRow key={run.id} {...rowActivateProps(openRun(run.id), "group")}>
                <TableCell className="text-xs tabular-nums">
                  <Link
                    to={nextgenAdminEvalRunPath(run.id)}
                    className="font-medium text-foreground hover:underline"
                  >
                    {formatEvalTimestamp(parseApiTimestamp(run.started_at))}
                  </Link>
                </TableCell>
                <TableCell>
                  <RunStatusBadge status={run.status} />
                </TableCell>
                {/* What the run measured: the agent, over the orchestrator model
                    the runner recorded for it. Several agents can share a model,
                    so the model alone does not identify a run's target. */}
                <TableCell className="max-w-[200px] text-xs">
                  {run.agent_id ? (
                    <span
                      className={cn(
                        "block truncate font-medium",
                        agentLabels.isNamed(run.agent_id)
                          ? "text-foreground"
                          : "italic text-muted-foreground"
                      )}
                      title={run.agent_id}
                    >
                      {agentLabels.agentLabel(run.agent_id)}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                  {run.model_name && (
                    <span
                      className="mt-0.5 block truncate font-mono text-[11px] text-muted-foreground"
                      title={run.model_name}
                    >
                      {shortModelName(run.model_name)}
                    </span>
                  )}
                </TableCell>
                <TableCell className="max-w-[200px]">
                  <TagChips tags={run.tags} emptyLabel="All cases" />
                </TableCell>
                <TableCell className="text-right text-xs">
                  {outcome(
                    (it) => (
                      <ScoreValue score={it.meanScore} />
                    ),
                    "ml-auto w-12"
                  )}
                </TableCell>
                {/* Cases covered, not results recorded. */}
                <TableCell className="text-right text-xs tabular-nums">
                  {outcome((it) => it.distinctCaseCount || "—", "ml-auto w-6")}
                </TableCell>
                {/* Launch parameters, not outcomes: they come off the run's own
                    record, so they are known the moment the row is, no gate. */}
                <TableCell className="text-right text-xs tabular-nums text-muted-foreground">
                  {run.run_parameters.iterations ?? "—"}
                </TableCell>
                <TableCell className="text-right text-xs tabular-nums text-muted-foreground">
                  {run.run_parameters.max_concurrency ?? "—"}
                </TableCell>
                <TableCell className="text-right text-xs tabular-nums text-muted-foreground">
                  {formatDuration(durationSeconds)}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
                        aria-label={`Actions for the run started ${formatEvalTimestamp(parseApiTimestamp(run.started_at))}`}
                      >
                        <MoreHorizontal className="size-4" aria-hidden />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild>
                        <Link to={nextgenAdminEvalRunPath(run.id)}>
                          <SquareArrowOutUpRight className="size-4" aria-hidden />
                          Open run
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem variant="destructive" onClick={() => setPendingDelete(run)}>
                        <Trash2 className="size-4" aria-hidden />
                        Delete run
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </EvalTable>

      <ConfirmDeleteDialog
        open={pendingDelete != null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="Delete this run?"
        description={
          <>
            The run and every result it recorded are removed. The cases themselves are untouched.
            {pendingDelete && (
              <span className="mt-2 block text-foreground">
                Started {formatEvalTimestamp(parseApiTimestamp(pendingDelete.started_at))}
                {pendingDelete.tags.length > 0 && ` · ${pendingDelete.tags.join(", ")}`}
              </span>
            )}
          </>
        }
        confirmLabel="Delete run"
        path={pendingDelete ? evalRunPath(pendingDelete.id) : null}
        errorMessage="Could not delete the run."
        onDeleted={onDeleted}
      />
    </>
  );
};
